import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { DOCUMENT_TYPES } from '../constants';
import { generateDocumentStream } from '../services/geminiService';
import type { FormData, FormErrors, Tone } from '../types';
import Icon from '../components/Icon';
import { saveGeneratedDocument } from '../utils/storage';

const GeneratePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const documentType = DOCUMENT_TYPES.find(doc => doc.id === id);
  const contentRef = useRef<HTMLTextAreaElement>(null);

  const [formData, setFormData] = useState<FormData>({
    businessName: '',
    websiteUrl: '',
    email: '',
    businessDescription: '',
    language: 'Arabic',
    tone: 'رسمي',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [generatedContent]);

  useEffect(() => {
    const savedData = localStorage.getItem('formData');
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      setFormData(prev => ({ ...prev, ...parsedData }));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('formData', JSON.stringify(formData));
  }, [formData]);

  const validateForm = useCallback(() => {
    const newErrors: FormErrors = {};
    if (!formData.businessName) newErrors.businessName = 'اسم الموقع مطلوب';
    if (!formData.websiteUrl) {
      newErrors.websiteUrl = 'رابط الموقع مطلوب';
    } else {
        try {
            let url = formData.websiteUrl;
            if (!/^https?:\/\//i.test(url)) {
                url = `http://${url}`;
            }
            const urlObject = new URL(url);
            if (!urlObject.hostname.includes('.')) {
                throw new Error("Hostname must contain a dot.");
            }
        } catch (_) {
            newErrors.websiteUrl = 'صيغة الرابط غير صحيحة';
        }
    }
    if (!formData.email) {
      newErrors.email = 'البريد الإلكتروني مطلوب';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'صيغة البريد الإلكتروني غير صحيحة';
    }
    if (!formData.businessDescription) newErrors.businessDescription = 'نبذة عن النشاط التجاري مطلوبة';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleGenerate = async () => {
    if (!validateForm() || !documentType) return;
    setIsLoading(true);
    setGeneratedContent('');
    setIsEditing(false);
    try {
      let fullContent = '';
      const stream = generateDocumentStream(documentType.title, formData);
      for await (const chunk of stream) {
        fullContent += chunk;
        setGeneratedContent(prev => prev + chunk);
      }
      if (fullContent && !fullContent.includes('--- حدث خطأ')) {
        saveGeneratedDocument({
          title: documentType.title,
          documentTypeId: documentType.id,
          content: fullContent,
        });
      }
    } catch (error) {
      console.error(error);
      setGeneratedContent('حدث خطأ أثناء إنشاء المستند. يرجى المحاولة مرة أخرى.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedContent);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  if (!documentType) {
    return (
      <div className="text-center py-20">
        <h1 className="text-2xl font-bold text-[var(--color-text-heading)]">نوع المستند غير موجود</h1>
        <Link to="/" className="text-[var(--color-primary)] hover:underline mt-4 inline-block">
          العودة إلى الصفحة الرئيسية
        </Link>
      </div>
    );
  }

  const tones: Tone[] = ['رسمي', 'ودي', 'احترافي'];

  const renderInput = (id: keyof FormData, label: string, type: string = 'text', isTextarea: boolean = false) => (
     <div>
        <label htmlFor={id} className="block text-sm font-medium text-[var(--color-text-heading)] mb-1">{label}:</label>
        <div className="relative">
             {isTextarea ? (
                <textarea
                    id={id}
                    value={formData[id]}
                    onChange={(e) => setFormData({ ...formData, [id]: e.target.value })}
                    rows={4}
                    className={`w-full p-2 border ${errors[id] ? 'border-[var(--color-danger)]' : 'border-[var(--color-border)]'} rounded-md shadow-sm focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] bg-[var(--color-input-bg)] text-[var(--color-text-body)]`}
                />
             ) : (
                <input
                    type={type}
                    id={id}
                    value={formData[id]}
                    onChange={(e) => setFormData({ ...formData, [id]: e.target.value })}
                    className={`w-full p-2 border ${errors[id] ? 'border-[var(--color-danger)]' : 'border-[var(--color-border)]'} rounded-md shadow-sm focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] bg-[var(--color-input-bg)] text-[var(--color-text-body)]`}
                />
             )}
             {errors[id] && (
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon name="AlertCircle" className="h-5 w-5 text-[var(--color-danger)]" />
                </div>
            )}
        </div>
        {errors[id] && <p className="mt-1 text-sm text-[var(--color-danger)]">{errors[id]}</p>}
    </div>
  );

  return (
    <div className="px-4 sm:px-6 lg:px-[70px] py-12">
      <Link to="/" className="inline-flex items-center gap-2 text-[var(--color-text-body)] hover:text-[var(--color-primary)] mb-6 group">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transition-transform group-hover:-translate-x-1" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
        العودة إلى القوالب
      </Link>
      <h1 className="text-3xl font-bold text-[var(--color-text-heading)] mb-8">إنشاء: {documentType.title}</h1>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
        <div className="bg-[var(--color-surface)] p-8 rounded-xl shadow-lg border border-[var(--color-border)]">
          <h2 className="text-2xl font-semibold mb-6 text-[var(--color-text-heading)]">معلومات النشاط التجاري</h2>
          <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); handleGenerate(); }}>
            {renderInput('businessName', 'اسم الموقع')}
            {renderInput('websiteUrl', 'رابط الموقع', 'text')}
            {renderInput('email', 'البريد الإلكتروني', 'email')}
            {renderInput('businessDescription', 'نبذة عن النشاط التجاري', 'text', true)}
             <div>
                <label className="block text-sm font-medium text-[var(--color-text-heading)] mb-2">نبرة الخطاب:</label>
                <div className="flex gap-2 rounded-lg p-1 bg-[var(--color-input-bg)] border border-[var(--color-border)]">
                  {tones.map((tone) => (
                    <button
                      key={tone}
                      type="button"
                      onClick={() => setFormData({ ...formData, tone })}
                      className={`flex-1 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors duration-200 focus:outline-none ${
                        formData.tone === tone
                          ? 'bg-[var(--color-bg-end)] text-[var(--color-text-heading)] shadow'
                          : 'text-[var(--color-text-body)] hover:bg-[var(--color-primary-light)]'
                      }`}
                    >
                      {tone}
                    </button>
                  ))}
                </div>
              </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-heading)] mb-2">اللغة:</label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input type="radio" name="language" value="Arabic" checked={formData.language === 'Arabic'} onChange={() => setFormData({...formData, language: 'Arabic'})} className="form-radio h-4 w-4 text-[var(--color-primary)] border-[var(--color-border)] focus:ring-[var(--color-primary)] bg-[var(--color-input-bg)]" />
                  <span className="mr-2 text-[var(--color-text-body)]">العربية</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="language" value="English" checked={formData.language === 'English'} onChange={() => setFormData({...formData, language: 'English'})} className="form-radio h-4 w-4 text-[var(--color-primary)] border-[var(--color-border)] focus:ring-[var(--color-primary)] bg-[var(--color-input-bg)]" />
                  <span className="mr-2 text-[var(--color-text-body)]">English</span>
                </label>
              </div>
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[var(--color-primary)] text-[var(--color-white)] font-bold py-3 px-4 rounded-lg hover:bg-[var(--color-primary-hover)] transition-colors duration-200 flex items-center justify-center disabled:bg-[var(--color-primary)]/50"
            >
              {isLoading ? (
                <>
                  <Icon name="Loader2" className="animate-spin w-5 h-5 mr-2" />
                  جاري الإنشاء...
                </>
              ) : (
                'إنشاء الصفحة'
              )}
            </button>
          </form>
        </div>
        <div className="bg-[var(--color-surface)] p-8 rounded-xl shadow-lg border border-[var(--color-border)] flex flex-col">
           <div className="flex justify-between items-center mb-4">
             <h2 className="text-2xl font-semibold text-[var(--color-text-heading)]">المحتوى المُنشأ</h2>
           </div>
          <div className="bg-[var(--color-input-bg)]/50 rounded-lg p-4 flex-grow w-full min-h-0">
            {isLoading && !generatedContent ? (
              <div className="flex flex-col items-center justify-center h-full text-[var(--color-text-body)]">
                <Icon name="Loader2" className="w-12 h-12 animate-spin text-[var(--color-primary)]" />
                <p className="mt-4 text-lg font-semibold">جاري إنشاء المستند...</p>
                <p className="mt-2 text-sm">يقوم الذكاء الاصطناعي بكتابة المحتوى.</p>
              </div>
            ) : generatedContent ? (
              <textarea
                ref={contentRef}
                value={generatedContent}
                readOnly={!isEditing}
                onChange={(e) => setGeneratedContent(e.target.value)}
                className={`w-full h-full bg-transparent border-none focus:ring-0 resize-none text-[var(--color-text-body)] ${isEditing ? 'cursor-text' : 'cursor-default'}`}
                placeholder="...يبدأ الذكاء الاصطناعي بالكتابة"
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-[var(--color-text-body)]/70">
                <Icon name="File" className="w-16 h-16 mb-4" />
                <p>سيظهر المحتوى الذي تم إنشاؤه هنا.</p>
              </div>
            )}
          </div>
          {generatedContent && !isLoading && (
             <div className="flex flex-col sm:flex-row gap-3 mt-4">
                <button
                    onClick={handleCopy}
                    className={`w-full sm:w-auto flex-grow flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-bold transition-colors ${
                    isCopied
                        ? 'bg-[var(--color-success)] text-[var(--color-white)]'
                        : 'bg-[var(--color-text-heading)] text-[var(--color-bg-start)] hover:bg-opacity-90'
                    }`}
                >
                    {isCopied ? <Icon name="Check" className="w-5 h-5" /> : <Icon name="Copy" className="w-5 h-5" />}
                    {isCopied ? 'تم النسخ!' : 'نسخ المحتوى'}
                </button>
                {!isEditing && (
                    <button
                        onClick={() => setIsEditing(true)}
                        className="w-full sm:w-auto flex-grow flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-bold bg-[var(--color-primary)] text-[var(--color-white)] hover:bg-[var(--color-primary-hover)] transition-colors"
                    >
                        <Icon name="Pencil" className="w-5 h-5" />
                        تعديل
                    </button>
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GeneratePage;